import { EventManager, Patch } from "@ploy-lib/calculation";
import { isNotNull } from "@ploy-lib/core";
import { MinimalInitialData } from "@ploy-lib/rest-resources";
import { parseSearchParams } from "@ploy-lib/routing";

interface ExternalQueryStringParamsSession {
	agent: string;
	vendorId?: string;
	token?: string;
}

interface ExternalQueryStringParamsInstructions extends MinimalInitialData {
	marketId?: string;

	objectPrice?: string;
	objectPriceExVat?: string;
	objectPriceIncVat?: string;
	equity?: string;
	equityPercent?: string;
	termsInYears?: string;
	totalTermsInYears?: string;
	totalTermsCountInYears?: string;
	termsCount?: string;
	campaignId?: string;

	objectTypeId?: number;
	objectSubTypeId?: number;
	objectCondition?: number;
	make?: string;
	model?: string;
	yearModel?: string;
	manualDefined?: boolean;
	mileage?: string;
	regNumber?: string;
	objectImageUrl?: string;

	customerTypeId?: number;
}

const alternativeMapping: Record<
	string,
	keyof ExternalQueryStringParamsInstructions
> = {
	finnkode: "marketId",
	pris: "objectPrice",
	egenkapital: "equity",
	nedbetalingstid: "termsInYears",
	// nybil is supposedly boolean, will this work?
	nybil: "objectCondition",
	arsmodell: "yearModel",
	merke: "make",
	modell: "model",
	kilometerstand: "mileage",
	registreringsnummer: "regNumber",
	bilde: "objectImageUrl",
	kampanjeid: "campaignId"
} as const;

type ExternalQueryStringParams = ExternalQueryStringParamsSession &
	ExternalQueryStringParamsInstructions;

type DefinedPatch = Patch<string, string | number | boolean>;
type Param = Partial<Omit<DefinedPatch, "value">> &
	Pick<DefinedPatch, "target" | "namespace"> & {
		source: keyof ExternalQueryStringParamsInstructions;
	};

const paramMapping: Param[] = [
	{
		source: "marketId",
		namespace: "Object",
		target: "ExternalReference"
	},
	{
		source: "marketId",
		namespace: "Object",
		target: "MarketplaceSearch",
		clickTrigger: true
	},
	{
		source: "marketId",
		namespace: "Object",
		target: "MarketplaceAndRegNrSearch",
		clickTrigger: true
	},

	// Equity _must_ be set first due some weird issue in calculator and field functions
	{ source: "equityPercent", namespace: "Calculator", target: "EquityPercent" },
	{ source: "equity", namespace: "Calculator", target: "Equity" },
	{ source: "objectPrice", namespace: "Calculator", target: "ObjectPrice" },
	{ source: "objectPriceExVat", namespace: "Calculator", target: "ObjectPriceExVat" },
	{ source: "objectPriceIncVat", namespace: "Calculator", target: "ObjectPriceIncVat" },
	// Bit unsure about the foloowing three, Calculator.TermsInYears sets the _first_ payeries
	{
		source: "termsInYears",
		namespace: "Calculator",
		target: "TermsInYears"
	},
	// These two set the _total_ terms, these would probably be the best match wrt. campains
	{
		source: "totalTermsCountInYears",
		namespace: "Calculator",
		target: "TotalTermsCountInYears"
	},
	{
		source: "totalTermsInYears",
		namespace: "Calculator",
		target: "TotalTermsInYears"
	},
	{ source: "termsCount", namespace: "Calculator", target: "TermsCount_0" },
	{ source: "campaignId", namespace: "Calculator", target: "Campaign" },
	{ source: "make", namespace: "Object", target: "Make" },
	{ source: "model", namespace: "Object", target: "Model" },
	{ source: "yearModel", namespace: "Object", target: "YearModel" },
	{ source: "manualDefined", namespace: "Object", target: "IsManualDefined" },
	{ source: "mileage", namespace: "Object", target: "Mileage" },
	{ source: "regNumber", namespace: "Object", target: "RegNumber" },
	{ source: "objectImageUrl", namespace: "Object", target: "ImageUrl" }

	// Handled init init of app instead, see ProductView in @ploy-ui/form-views
	// { source: "customerTypeId", namespace: "Customer", target: "CustomerType" }
	// { source: "objectTypeId", namespace: "Object", target: "Category" },
	// { source: "objectSubTypeId", namespace: "Object", target: "CarType" },
	// { source: "objectCondition", namespace: "Object", target: "ObjectCondition" },
];

export function parseQueryStringParams(
	queryString: string
): (ExternalQueryStringParams & { cookieless?: string }) | null {
	const qsParams = parseSearchParams<
		{
			ext?: ExternalQueryStringParams;
			cookieless?: string;
		} & Record<string, any>
	>(queryString);

	if (!(qsParams?.ext?.agent || qsParams?.ext?.token)) return null;

	const params = qsParams.ext;

	const { ext, ...otherParams } = qsParams;

	for (let [alternativeName, extName] of Object.entries(alternativeMapping)) {
		// Only set alternative value if the target is unset
		if (params[extName] == null && otherParams[alternativeName] != null) {
			params[extName] = otherParams[alternativeName];
		}
	}

	return { ...params, cookieless: qsParams.cookieless ?? undefined };
}

export const bootstrapQueryStringForAgentSetup = (
	eventProvider: EventManager<string, string | number | boolean>
) => {
	const unsubscribe = eventProvider.calculatorReady.subscribe(() => {
		unsubscribe();

		const commonProps: Partial<DefinedPatch> = {
			overwrite: true,
			isResolve: true,
			noDefer: true
		};

		const queryString = window.location.search;
		const params = parseQueryStringParams(queryString);

		if (!params) return;

		if (params["manualDefined"] === undefined) params["manualDefined"] = true;

		const patches: DefinedPatch[] = paramMapping
			.map(({ source, ...patch }) => {
				if (params[source] == null) return null;

				return {
					...commonProps,
					...patch,
					value: params[source]
				};
			})
			.filter(isNotNull);

		if (patches.length > 0) {
			patches.push({
				...commonProps,
				namespace: "Calculator",
				target: "SoftVal",
				value: 0
			});
			eventProvider.patch.notify({ patches });
		}
	});
};
